import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { NativeSelect } from "@/components/common";
import { LogoUpload } from "@/components/LogoUpload";
import { NotaCard, DEFAULT_DESIGN } from "@/components/NotaCard";
import { api, errMsg } from "@/lib/api";
import { currentPeriod, notaPreview } from "@/lib/format";

const SWATCHES = ["#0f172a", "#059669", "#0369a1", "#b91c1c", "#c2410c", "#7c2d12", "#1d4ed8", "#4d7c0f"];
const TOGGLES = [
  ["show_logo", "Logo perusahaan"], ["show_company_address", "Alamat perusahaan"], ["show_company_phone", "Telepon perusahaan"],
  ["show_period", "Periode"], ["show_customer_address", "Alamat pelanggan"], ["show_customer_phone", "Telepon pelanggan"],
  ["show_price", "Kolom harga satuan"], ["show_prev_debt", "Sisa hutang periode sebelumnya"], ["show_footer", "Catatan kaki"], ["show_signature", "Tanda tangan"], ["show_stamp", "Stempel"],
];
const SAMPLE = {
  id: "sample", number: "AF10-0001", period: currentPeriod(), date: new Date().toISOString().slice(0, 10),
  customer_name: "Contoh Pelanggan", customer_address: "Dusun Krajan, RT 01", customer_phone: "0812-0000-0000", total: 160000,
  prev_debt: { total: 45000, periods: [{ period: "2026-08", remaining: 45000 }] },
  lines: [{ voucher_type_id: "a", voucher_name: "Voucher 1 Hari", qty: 30, price: 3000, total: 90000 },
    { voucher_type_id: "b", voucher_name: "Voucher 3 Hari", qty: 10, price: 7000, total: 70000 }],
};

function AssetField({ kind, label, img, disabled, onChange }) {
  const remove = async () => {
    try { await api.delete(`/company/nota-asset/${kind}`); onChange(null); toast.success(`${label} dihapus`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
      <div className="flex items-start gap-2">
        <LogoUpload logo={img} uploadUrl={`/company/nota-asset/${kind}`} disabled={disabled} onUploaded={onChange} label={`Unggah ${label}`} testId={`nota-${kind}`} />
        {img && !disabled && <Button size="sm" variant="ghost" className="text-rose-600" onClick={remove} data-testid={`nota-${kind}-remove`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

export function NotaDesigner({ company, disabled, onSaved }) {
  const [d, setD] = useState(DEFAULT_DESIGN);
  const [rows, setRows] = useState(4);
  useEffect(() => {
    setD({ ...DEFAULT_DESIGN, ...(company.nota_design || {}) });
  }, [company]);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const { signature_img, stamp_img, ...body } = d;
    try { onSaved((await api.put("/company/nota-design", body)).data); toast.success("Desain nota disimpan"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid xl:grid-cols-[1fr_auto] gap-6" data-testid="nota-designer">
      <div className="space-y-5 min-w-0">
        <div>
          <Label className="text-xs font-semibold">Warna Aksen</Label>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {SWATCHES.map((c) => (
              <button key={c} type="button" disabled={disabled} onClick={() => set("accent_color", c)} style={{ background: c }}
                className={`h-7 w-7 rounded-sm border-2 transition-transform hover:scale-110 ${d.accent_color === c ? "border-amber-400" : "border-white shadow"}`} data-testid={`nota-color-${c.slice(1)}`} />
            ))}
            <input type="color" value={d.accent_color} disabled={disabled} onChange={(e) => set("accent_color", e.target.value)} className="h-7 w-10 cursor-pointer" data-testid="nota-color-input" />
            <span className="font-mono text-xs">{d.accent_color}</span>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs font-semibold">Gaya Kepala Nota</Label>
            <NativeSelect value={d.header_style} disabled={disabled} onChange={(e) => set("header_style", e.target.value)} className="w-full mt-1" data-testid="nota-header-style">
              <option value="line">Garis bawah</option><option value="solid">Blok warna</option>
            </NativeSelect></div>
          <div><Label className="text-xs font-semibold">Garis Potong</Label>
            <NativeSelect value={d.border_style} disabled={disabled} onChange={(e) => set("border_style", e.target.value)} className="w-full mt-1" data-testid="nota-border-style">
              <option value="dashed">Putus-putus</option><option value="solid">Garis penuh</option><option value="none">Tanpa garis</option>
            </NativeSelect></div>
          <div><Label className="text-xs font-semibold">Judul Nota</Label><Input value={d.title} disabled={disabled} maxLength={30} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="nota-title-input" /></div>
        </div>
        <div>
          <Label className="text-xs font-semibold">Kolom & Bagian yang Ditampilkan</Label>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {TOGGLES.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-3 py-1.5 bg-white">
                <Switch checked={!!d[k]} disabled={disabled} onCheckedChange={(v) => set(k, v)} data-testid={`nota-toggle-${k}`} />{l}
              </label>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs font-semibold">Label Tanda Tangan</Label><Input value={d.signature_label} disabled={disabled} onChange={(e) => set("signature_label", e.target.value)} className="mt-1" data-testid="nota-signature-label" /></div>
          <div><Label className="text-xs font-semibold">Nama Penanda Tangan</Label><Input value={d.signer_name} placeholder={company.name} disabled={disabled} onChange={(e) => set("signer_name", e.target.value)} className="mt-1" data-testid="nota-signer-name" /></div>
          <div><Label className="text-xs font-semibold">Cap Status (mis. LUNAS)</Label><Input value={d.paid_label} disabled={disabled} maxLength={20} onChange={(e) => set("paid_label", e.target.value)} className="mt-1" data-testid="nota-paid-label" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <AssetField kind="signature" label="Tanda Tangan" img={d.signature_img} disabled={disabled} onChange={(v) => set("signature_img", v)} />
          <AssetField kind="stamp" label="Stempel" img={d.stamp_img} disabled={disabled} onChange={(v) => { set("stamp_img", v); if (v) set("show_stamp", true); }} />
        </div>
        {!disabled && <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="nota-design-save">Simpan Desain Nota</Button>}
      </div>
      <div className="min-w-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-700">Pratinjau langsung</span>
          <NativeSelect value={rows} onChange={(e) => setRows(Number(e.target.value))} className="h-8 text-xs" data-testid="nota-preview-size">
            <option value={4}>Ukuran 8/hal</option><option value={5}>Ukuran 10/hal</option><option value={3}>Ukuran 6/hal</option>
          </NativeSelect>
        </div>
        <div className="bg-slate-200/70 border border-slate-300 p-4 rounded-sm overflow-auto">
          <div className="bg-white shadow-lg mx-auto" style={{ width: "99mm", height: `${285 / rows}mm`, display: "flex" }} data-testid="nota-preview">
            <div className="flex-1 flex [&>.nota]:w-full"><NotaCard inv={{ ...SAMPLE, number: notaPreview(company.nota_code, company.extra_code) }} tenant={{ ...company, nota_design: d }} rows={rows} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
